import * as React from 'react';

import { UserContext } from '../Context/UserContext';
import { UserCallLogRepository } from '../../MedicareProAPI/Repositories/User/UserCallLogRepository';
import type { UserCallLog } from '../../MedicareProAPI/Entities/User/UserCallLog';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import Loader from '../Components/Loader';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import StyledModal from '../Components/StyledModal';

export interface AgentLogsCallsPanelProps {}

export default function AgentLogsCallsPanel(props: AgentLogsCallsPanelProps): React.JSX.Element {
  const [callLogsLoading, setCallLogsLoading] = React.useState<boolean>(true);
  const [callLogs, setCallLogs] = React.useState<UserCallLog[]>(null);
  const [selectedNumber, setSelectedNumber] = React.useState<string>(null);
  const [selectedNumberIndLoading, setSelectedNumberIndLoading] = React.useState<boolean>(false);
  const [selectedNumberInd, setSelectedNumberInd] = React.useState<Ind>(null);

  const [callLogsAssignModal, setCallLogsAssignModal] = React.useState<boolean>(false);

  const userData = React.useContext(UserContext);

  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(userData.userAccessToken);

  const userCallLogRepository = new UserCallLogRepository();
  userCallLogRepository.setAccessToken(userData.userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userData.userAccessToken);

  React.useEffect(() => {
    loadCallLogs(userData.userId);
  }, []);

  React.useEffect(() => {
    if (selectedNumber) {
      setSelectedNumberIndLoading(true);
      individualRepository
        .getIndByNumber(selectedNumber)
        .then((individual) => {
          setSelectedNumberInd(individual);
        })
        .catch((error) => {
          setSelectedNumberInd(null);
        })
        .finally(() => {
          setSelectedNumberIndLoading(false);
        });
    }
  }, [selectedNumber]);

  const loadCallLogs = (userId: number) => {
    setCallLogsLoading(true);
    setCallLogs(null);

    webAPI.getUserCallLogsGrouped(userId).then((callLogsGroupedResponse: any) => {
      setCallLogsLoading(false);
      setCallLogs(callLogsGroupedResponse.data);

      if (
        callLogsGroupedResponse.data &&
        callLogsGroupedResponse.data[0] &&
        callLogsGroupedResponse.data[0].number
      ) {
        setSelectedNumber(callLogsGroupedResponse.data[0].number);
      }
    });
  };

  const handleChangeNumber = (phoneNumber: string) => {
    setSelectedNumber(phoneNumber);
  };

  const handleAssignCallLogs = (phoneNumber: string) => {
    webAPI.assignCallLogsByPhone(userData.userId, phoneNumber).then((response) => {
      setSelectedNumber(null);
      loadCallLogs(userData.userId);
      setCallLogsAssignModal(true);
    });
  };

  return (
    <div className="card card-tall p-2">
      <StyledModal
        size="md"
        title="Call Logs Assigned"
        footerCloseButton={true}
        show={callLogsAssignModal}
        onClose={(e) => setCallLogsAssignModal(false)}
      >
        <p>The selected call logs have assigned.</p>
      </StyledModal>
      <div className="row">
        {callLogsLoading === true && (
          <div className="col-12">
            <Loader size="large" className="mt-4 mb-4" />
          </div>
        )}
        {callLogsLoading === false && callLogs && callLogs.length === 0 && (
          <div className="col-12">
            <p className="alert alert-info mt-2 mb-0">No unmatched call logs</p>
          </div>
        )}
        {callLogsLoading === false && callLogs && callLogs.length > 0 && (
          <>
            <div className="col-3" style={{ borderRight: '1px solid #dee2e6' }}>
              {callLogs.map((callLog: any) => {
                const classList = [
                  'small',
                  'card',
                  'p-2',
                  'mb-2',
                  'cp',
                  'card-tall',
                  'cp',
                  'border',
                  'border-info',
                ];

                if (callLog.number === selectedNumber) {
                  classList.push('bg-info');
                  classList.push('text-white');
                }

                return (
                  <div
                    key={callLog.number}
                    className={classList.join(' ')}
                    onClick={(e) => handleChangeNumber(callLog.number)}
                  >
                    <div>
                      <span className="float-right small mt-1">
                        {callLog.total > 1 ? `${callLog.total} calls` : `1 call`}
                      </span>
                      <p className="mt-0 mb-0">
                        <strong>{Utilities.phoneNumberDisplay(callLog.number)}</strong>
                      </p>
                      <p className="mt-1 mb-0">{callLog.name ? callLog.name : <em>Unknown</em>}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="col-9">
              {selectedNumber && (
                <>
                  <div className="card card-tall p-2 border border-info">
                    <p className="mt-0 mb-0">
                      <strong>{Utilities.phoneNumberDisplay(selectedNumber)}</strong>
                    </p>
                    {selectedNumberIndLoading === true && (
                      <Loader size="large" className="mt-4 mb-4" />
                    )}
                    {selectedNumberIndLoading === false && selectedNumberInd === null && (
                      <p className="alert alert-info mt-2 mb-0">No matches for this number</p>
                    )}
                    {selectedNumberIndLoading === false && selectedNumberInd !== null && (
                      <>
                        <p className="alert alert-info mt-2 mb-0">
                          Call matches{' '}
                          <strong>
                            <a
                              target="_blank"
                              href={`/agent/profileController/index/${selectedNumberInd.getIndId()}`}
                              rel="noreferrer"
                            >
                              {selectedNumberInd.getIndName()}
                            </a>
                          </strong>
                        </p>
                        <div className="mt-2">
                          <button
                            onClick={(e) => handleAssignCallLogs(selectedNumber)}
                            className="btn btn-success"
                          >
                            Assign Logs to {selectedNumberInd.getIndName()}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
